import { useState, useEffect } from 'react';
import axiosInstance from '../../services/axiosInstance';

// LRU blob URL cache — keeps memory bounded and revokes old object URLs
const MAX_CACHE_SIZE = 100;
const blobCache = new Map<string, string>();

async function getMediaBlobUrl(path: string): Promise<string> {
  if (blobCache.has(path)) return blobCache.get(path)!;

  const res = await axiosInstance.get<Blob>(
    `/api/v1/chat/media/serve/${path}`,
    { responseType: 'blob' },
  );

  // Evict oldest entry if we're over the limit
  if (blobCache.size >= MAX_CACHE_SIZE) {
    const firstKey = blobCache.keys().next().value as string;
    URL.revokeObjectURL(blobCache.get(firstKey)!);
    blobCache.delete(firstKey);
  }

  const url = URL.createObjectURL(res.data);
  blobCache.set(path, url);
  return url;
}

/**
 * Resolves chat media paths to local blob:// URLs.
 *
 * - Fetches each image through the API (JWT auth via axiosInstance).
 * - Caches blobs in-memory so re-renders are instant.
 * - MinIO stays private — browser never talks to it directly.
 */
export function useMediaBlobUrls(mediaPaths: string[]): Record<string, string> {
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!mediaPaths.length) return;

    const unfetched = mediaPaths.filter((p) => !blobUrls[p]);
    if (!unfetched.length) return;

    Promise.all(
      unfetched.map((path) =>
        getMediaBlobUrl(path)
          .then((url) => [path, url] as const)
          .catch(() => [path, ''] as const),
      ),
    ).then((pairs) => {
      const valid = pairs.filter(([, url]) => url);

      if (valid.length) {
        setBlobUrls((prev) => ({ ...prev, ...Object.fromEntries(valid) }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaPaths.join(',')]);

  return blobUrls;
}
